/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import React, { useEffect } from 'react';
import {
  TagOptionRecord,
  TagSelectInputProps,
  TagSelectInputState,
  SelectSingleOptionAction,
  RemoveSingleOptionAction,
  ChangeOptionVisibilityAction,
  FilterTagsAction,
  AddSingleOptionAction,
  Actions,
} from '../types';
import useDidUpdateEffect from '../hooks/useDidUpdateEffects';
import { tagSelectInputReducer } from '../reducer/tagSelectInput.reducer';
import TagSelectOption from './TagSelectOption';
import TagSingleOption from './TagSingleOption';
import { nullFn, isEmpty, isOptionSelected, optionExists } from '../utils';
import { CustomClassContainer } from '../../utility';

function TagSelectInput<T extends TagOptionRecord>({
  label = '',
  name = '',
  data,
  selected = [],
  placeholder,
  classes = [],
  keyField,
  valueField,
  max = Number.MAX_VALUE,
  allowInput = true,
  onSelectedOptionsChange = nullFn,
  showOnFocus = false,
}: TagSelectInputProps<T>): JSX.Element {
  const [tag, setTag] = React.useState('');
  const inputRef = React.useRef<HTMLInputElement>(null);
  const initialState: TagSelectInputState<T> = React.useMemo(
    () => ({
      options: data ? data : [],
      maxSelected: max,
      filteredOptions: [],
      selectedOptions: selected,
      optionsVisiable: false,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const [
    { filteredOptions, selectedOptions, optionsVisiable, maxSelected },
    dispatch,
  ] = React.useReducer<React.Reducer<TagSelectInputState<T>, Actions<T>>>(
    tagSelectInputReducer,
    initialState,
  );

  const handleSelectOption = (action: SelectSingleOptionAction<T>) => {
    dispatch(action);
    hideOptions();
    setTag('');
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleRemoveOption = (action: RemoveSingleOptionAction<T>) => {
    dispatch(action);
  };

  const handleOptionVisibility = (action: ChangeOptionVisibilityAction) => {
    dispatch(action);
  };

  const handleFilterTags = (action: FilterTagsAction) => {
    dispatch(action);
  };

  const handleAddOption = (action: AddSingleOptionAction) => {
    if (!allowInput) return;
    dispatch(action);
  };

  useDidUpdateEffect(() => {
    onSelectedOptionsChange(selectedOptions);
  }, [selectedOptions]);

  useEffect(() => {
    if (data !== initialState.options)
      dispatch({
        type: 'SET_DATA_OPTIONS',
        data: data,
        selected: selected,
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '' && showOnFocus === false) {
      hideOptions();
    } else {
      showOptions();
    }

    setTag(value);
    filerTagsOnChange(value);
  };

  const showOptions = () => {
    if (optionsVisiable) return;
    handleOptionVisibility({
      type: 'CHANGE_OPTION_VISIBILITY',
      visibility: true,
      showOnFocus: showOnFocus,
    });
  };

  const handleOnFocus = () => {
    if (!showOnFocus) return;

    handleOptionVisibility({
      type: 'CHANGE_OPTION_VISIBILITY',
      visibility: true,
      showOnFocus: showOnFocus,
    });
  };

  const hideOptions = () => {
    handleOptionVisibility({
      type: 'CHANGE_OPTION_VISIBILITY',
      visibility: false,
      showOnFocus: showOnFocus,
    });
  };

  const filerTagsOnChange = (value: string) => {
    handleFilterTags({
      type: 'FILTER_TAGS',
      value: value,
      valueField: valueField,
    });
  };

  const handleDoneClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (!allowInput) {
      hideOptions();
      setTag('');
      return;
    }
    if (tag === '') return;
    if (optionExists(selectedOptions, tag, valueField) !== -1) return;

    handleAddOption({
      type: 'ADD_NEW_OPTION',
      valueField,
      value: tag,
    });

    hideOptions();
    setTag('');
  };

  const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (!allowInput) {
        hideOptions();
        setTag('');
        return;
      }
      if (tag === '') return;
      if (optionExists(selectedOptions, tag, valueField) !== -1) return;
      handleAddOption({
        type: 'ADD_NEW_OPTION',
        valueField,
        value: tag,
      });
      hideOptions();
      setTag('');
    }
  };

  return (
    <CustomClassContainer classes={classes}>
      <div className='flex w-auto flex-col'>
        <div className='mb-2 flex flex-row items-center justify-between'>
          <label
            className='text-base font-medium tracking-tight text-gray-dark'
            htmlFor={name}
          >
            {label}
          </label>
        </div>
        <div className='tags-container relative'>
          <div
            className={`relative flex flex-row flex-wrap items-center overflow-hidden overflow-y-auto rounded-lg border border-gray-light px-1 py-2 shadow-sm ${
              optionsVisiable ? 'pr-28' : ''
            }`}
          >
            {selectedOptions.map((option, idx) => (
              <TagSingleOption
                key={idx}
                option={option}
                valueField={valueField}
                keyField={keyField}
                onRemoveOption={handleRemoveOption}
              />
            ))}
            <input
              ref={inputRef}
              onChange={handleInputChange}
              value={tag}
              onFocus={handleOnFocus}
              className='my-1 ml-1 mr-2 w-full flex-grow appearance-none rounded-md border border-transparent text-base font-medium placeholder-gray-light outline-none focus:border-transparent focus:outline-none focus:ring-transparent'
              id={name}
              name={name}
              placeholder={placeholder ? placeholder : ''}
              onKeyDown={onInputKeyDown}
              readOnly={selectedOptions.length === maxSelected}
            />
            {optionsVisiable && (
              <button
                onClick={handleDoneClick}
                type='button'
                className='absolute bottom-0 right-0 top-0 w-28 bg-green text-xs font-bold uppercase text-white'
              >
                Done
              </button>
            )}
          </div>

          {optionsVisiable ? (
            filteredOptions.length !== 0 ? (
              <div className='absolute left-0 right-0 top-full z-50 mt-2 overflow-hidden rounded-md border border-gray-light'>
                <ul className='flex w-full flex-col divide-y divide-gray-light '>
                  {filteredOptions.map((option, idx) => {
                    let id: number | string;
                    if (typeof option === 'object') {
                      id = keyField
                        ? !isEmpty(option[keyField])
                          ? option[keyField]
                          : idx
                        : idx;
                    } else {
                      id = idx;
                    }

                    const selected = isOptionSelected(
                      option,
                      selectedOptions,
                      keyField,
                    );
                    return (
                      <TagSelectOption
                        id={id}
                        key={id}
                        option={option}
                        selected={selected}
                        valueField={valueField}
                        optionIdx={idx}
                        keyField={keyField}
                        onOptionsSelected={handleSelectOption}
                      />
                    );
                  })}
                </ul>
              </div>
            ) : null
          ) : null}
        </div>
      </div>
    </CustomClassContainer>
  );
}

export default TagSelectInput;
